import { PenBoxIcon } from 'lucide-react';

import { EditGitSettingsButton } from '@/react/common/stacks/EditGitSettingsButton';
import { useStack } from '@/react/common/stacks/queries/useStack';
import { StackId } from '@/react/common/stacks/types';

import { LinkButton } from '@@/LinkButton';

import { Workflow } from '../types';
import { getDeployedStackLink } from '../utils';
import {
  getRealObject,
  isHelmObject,
  MixedArtifactObject,
} from '../queries/useArtifactRealObjects';

interface Link {
  to: string;
  params: object;
}

type EditTarget =
  | { type: 'stack'; stackId: StackId }
  | { type: 'helm' | 'workflow'; link: Link };

export function EditButton({
  workflow,
  artifactObjects,
}: {
  workflow: Workflow;
  artifactObjects: MixedArtifactObject[];
}) {
  const target = getEditTarget(workflow, artifactObjects);

  if (target.type === 'stack') {
    return <EditRegularStackButton stackId={target.stackId} />;
  }

  return (
    <LinkButton
      icon={PenBoxIcon}
      color="light"
      to={target.link.to}
      params={target.link.params}
      data-cy="workflow-edit-button"
    >
      Edit
    </LinkButton>
  );
}

function EditRegularStackButton({ stackId }: { stackId: StackId }) {
  const stackQuery = useStack(stackId);

  return <EditGitSettingsButton stack={stackQuery.data} label="Edit" />;
}

function getEditTarget(
  workflow: Workflow,
  artifactObjects: MixedArtifactObject[]
): EditTarget {
  const workflowTarget: EditTarget = {
    type: 'workflow',
    link: {
      to: 'portainer.gitops.workflows.edit',
      params: { workflowId: workflow.id },
    },
  };

  if (workflow.artifacts.length !== 1) {
    return workflowTarget;
  }

  const [artifact] = workflow.artifacts;
  const realObject = getRealObject(artifactObjects, artifact.id, artifact.type);

  if (realObject && isHelmObject(realObject)) {
    const link = getDeployedStackLink(artifact, artifactObjects);
    return link ? { type: 'helm', link } : workflowTarget;
  }

  if (artifact.type === 'stack') {
    return { type: 'stack', stackId: artifact.id };
  }

  return workflowTarget;
}
